import { AbstractDestroyable, Callback1, VoidCallback } from '@deqo/foundation';
import { idGenerator, invokeLater } from '../../CodesThatsGottaGoSomewhereElse';
import { Component, ComponentType, Convention, ObjectManager } from '../../concepts/objects';
import { EditorSession } from './EditorSession';
import { StatePropertyRef } from './ExtendableStore';
import { Physical } from './entities/Physical';
import { KeyedBurdens } from './extensibility/Burdens';

const nextId = idGenerator('s');

export class SelectionSet implements ReadonlySet<Physical> {

    public static readonly empty = SelectionSet.create();

    public static create(values?: Iterable<Physical>) {
        return new SelectionSet(new Set(values ?? []));
    }

    public readonly id = nextId();

    protected constructor(
        private readonly set: Set<Physical>) {
    }

    public get size(): number {
        return this.set.size;
    }

    public first(): Physical {
        for (const v of this.set) {
            return v;
        }
    }

    public firstAndOnly(): Physical {
        if (this.set.size !== 1) return null;
        for (const v of this.set) {
            return v;
        }
    }

    public all(predicate: Callback1<boolean, Physical>) {
        for (const v of this.set) {
            if (!predicate(v)) return false;
        }
        return true;
    }

    public some(predicate: Callback1<boolean, Physical>) {
        for (const v of this.set) {
            if (predicate(v)) return true;
        }
        return false;
    }

    public add(value: Physical): SelectionSet {
        return this.splice([value], null);
    }

    public addMany(values: Iterable<Physical>): SelectionSet {
        return this.splice(values, null);
    }

    public delete(value: Physical): SelectionSet {
        return this.splice(null, [value]);
    }

    public deleteMany(values: Iterable<Physical>): SelectionSet {
        return this.splice(null, values);
    }

    public splice(add?: Iterable<Physical>, del?: Iterable<Physical>) {
        const newValues = new Set(this.set);
        if (add) {
            for (const v of add) {
                newValues.add(v);
            }
        }
        if (del) {
            for (const v of del) {
                newValues.delete(v);
            }
        }
        return new SelectionSet(newValues);
    }

    public replace(values: Iterable<Physical>) {
        return SelectionSet.create(values);
    }

    public clear() {
        return SelectionSet.empty;
    }

    public forEach(callbackfn: (value: Physical, value2: Physical, set: ReadonlySet<Physical>) => void, thisArg?: any): void {
        return this.set.forEach(callbackfn, thisArg);
    }

    public has(value: Physical): boolean {
        return this.set.has(value);
    }

    public hasAll(values: Iterable<Physical>): boolean {
        for (const v of values) {
            if (!this.set.has(v)) return false;
        }
        return true;
    }

    public filter(predicate: Callback1<boolean, Physical>): Physical[] {
        const results = [] as Physical[];
        for (const v of this.set) {
            if (predicate(v)) results.push(v);
        }
        return results;
    }

    public polarize<T extends Component>(type: ComponentType<T> | Convention<T>): T[] {
        return this.filter(x => x.is(type)) as unknown as T[];
    }

    public locked() {
        for (const p of this.set) {
            if (p.alive && p.locked) return true;
        }
        return false;
    }

    public uniform(type?: ComponentType<Physical>) {
        if (!this.set.size) return true;
        let prev = null as Physical;
        for (const v of this.set) {
            if (!!type) {
                if (!v.is(type)) return false;
            }
            else {
                if (prev && v.type !== prev.type) return false;
            }
            prev = v;
        }
        return true;
    }

    public entries(): IterableIterator<[Physical, Physical]> {
        return this.set.entries();
    }

    public keys(): IterableIterator<Physical> {
        return this.set.keys();
    }

    public values(): IterableIterator<Physical> {
        return this.set.values();
    }

    [Symbol.iterator](): IterableIterator<Physical> {
        return this.set[Symbol.iterator]();
    }

    public toArray() {
        return Array.from(this.set);
    }

    public toArrayOfIds() {
        return Array.from(this.set).map(x => x.id);
    }
}

export const Selection = StatePropertyRef<SelectionSet>('selection');

export class SelectionWatch extends AbstractDestroyable {

    // Keep a set of the events/observers we are tracking.
    private trackers = null as KeyedBurdens;
    private enabled = false;
    private firing = false;

    constructor(private session: EditorSession, private objects: ObjectManager, private callback: VoidCallback) {
        super();
        this.enable();
        this.burden(() => this.disable());
    }

    public toggle(to?: boolean) {
        to = to ?? !this.enabled;
        if (to) {
            this.enable();
        }
        else {
            this.disable();
        }
    }

    public enable() {
        this.guardDestroyed();
        if (this.enabled) return;
        this.trackers = new KeyedBurdens();
        this.trackers.set('$', this.session.observe(Selection, s => {
            this.updateTrackers(s);
            this.fire();
        }));
        this.updateTrackers(this.session.get(Selection));
        this.enabled = true;
    }

    public disable() {
        if (!this.enabled) return;
        this.trackers.destroy();
        this.trackers = null;
        this.enabled = false;
    }

    private updateTrackers = (selection: SelectionSet) => {
        // Untrack components no longer selected and tracks selected components that we are not tracking already.
        const keep = new Set<string>('$');
        for (const { id } of selection) {
            keep.add(id);
            this.trackers.addMaybe(id, () => this.objects.on(id, 'affected', this.fire));
        }
        for (const id of this.trackers.keys()) {
            if (!keep.has(id)) {
                this.trackers.resolveMaybe(id);
            }
        }
    };

    private fire = () => {
        if (this.firing) return;
        this.firing = true;
        invokeLater(() => {
            this.callback();
            this.firing = false;
        });
    };
}
